import React, { useState, useRef, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Play, Pause, Volume2, VolumeX, RotateCcw, RotateCw, MapPin, X } from 'lucide-react';
import { PodcastMenuButton } from './PodcastMenuButton';

interface Podcast {
  Name: string;
  City: string;
  Country: string;
  Description_FR: string;
  Description_EN: string;
  AudioFile_FR: string | null;
  AudioFile_EN: string | null;
  Image: string | null;
  Latitude: number;
  Longitude: number;
}

interface AudioPlayerProps {
  podcast: Podcast | null;
  language: 'FR' | 'EN';
  onClose: () => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  podcast,
  language,
  onClose
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Get the appropriate audio file - fallback to other language if current not available
  const audioFile = podcast ? (() => {
    const preferredFile = language === 'FR' ? podcast.AudioFile_FR : podcast.AudioFile_EN;
    const fallbackFile = language === 'FR' ? podcast.AudioFile_EN : podcast.AudioFile_FR;
    return preferredFile || fallbackFile;
  })() : null;

  // Update audio source when podcast changes
  useEffect(() => {
    if (!podcast || !audioFile || !audioRef.current) return;

    const audio = audioRef.current;
    audio.src = audioFile;
    audio.load();
    
    // Reset state when podcast changes
    setIsPlaying(false);
    setCurrentTime(0);
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setCurrentTime(0);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [podcast, audioFile]);

  // Auto-play when a new podcast is selected
  useEffect(() => {
    if (!podcast || !audioFile || !audioRef.current) return;
    
    const audio = audioRef.current;
    const tryAutoPlay = () => {
      if (audio.readyState >= 2) { // HAVE_CURRENT_DATA
        audio.play().catch(console.warn);
      }
    };

    if (audio.readyState >= 2) {
      tryAutoPlay();
    } else {
      audio.addEventListener('canplay', tryAutoPlay, { once: true });
    }

    return () => {
      audio.removeEventListener('canplay', tryAutoPlay);
    };
  }, [podcast, audioFile]);

  // Play/pause functionality
  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Seek functionality
  const handleSeek = (value: number[]) => {
    if (!audioRef.current) return;
    const newTime = value[0];
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Volume control
  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  // Mute/unmute
  const toggleMute = () => {
    if (!audioRef.current) return;
    
    if (isMuted) {
      audioRef.current.volume = volume;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  // Skip functions
  const skipBackward = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, currentTime - 10);
  };

  const skipForward = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.min(duration, currentTime + 10);
  };

  // Playback speed control
  const playbackSpeeds = [1, 1.25, 1.5, 1.75, 0.75];
  
  const togglePlaybackSpeed = () => {
    const currentIndex = playbackSpeeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % playbackSpeeds.length;
    const newRate = playbackSpeeds[nextIndex];
    setPlaybackRate(newRate);
    
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  };

  // Apply playback rate when audio is loaded
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate, audioFile]);

  // Format time
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!podcast) return null;

  return (
    <Card className="fixed bottom-2 left-2 right-2 md:left-4 md:right-4 z-50 card-floating">
      <div className="relative" style={{ paddingTop: '0.3cm', paddingBottom: '0.3cm', paddingLeft: '8px', paddingRight: '4px' }}>
        
        {/* Boutons d'action en haut à droite */}
        <div className="flex justify-end gap-1 absolute top-0 right-0" style={{ marginTop: '0.3cm', marginRight: '0.2cm' }}>
          <PodcastMenuButton 
            podcastName={podcast.Name} 
            className="bg-background/80 hover:bg-background border border-border/50 backdrop-blur-sm w-7 h-7 p-0"
          />
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="w-7 h-7 p-0 rounded-full bg-background/80 hover:bg-background border border-border/50 backdrop-blur-sm"
            aria-label="Fermer le lecteur audio"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* Header avec image et informations */}
        <div className="flex items-center gap-3 mb-1 mt-6">
          {/* Image du podcast */}
          <div className="flex-shrink-0">
            {podcast.Image ? (
              <img
                src={podcast.Image}
                alt={podcast.Name}
                className="w-14 h-14 object-cover rounded-lg shadow-md"
              />
            ) : (
              <div className="w-14 h-14 bg-gradient-earth rounded-lg flex items-center justify-center shadow-md">
                <MapPin className="w-7 h-7 text-white/80" />
              </div>
            )}
          </div>
          
          {/* Informations du podcast */}
          <div className="flex-1 min-w-0 overflow-hidden pr-16">
            <div className="scroll-container relative overflow-hidden">
              <h3 className="scroll-text font-bold text-base leading-tight mb-0.5 whitespace-nowrap">
                {podcast.Name}
              </h3>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate font-medium">{podcast.City}, {podcast.Country}</span>
            </div>
          </div>
        </div>

        {/* Barre de progression améliorée */}
        <div className="mb-1">
          <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
            <span className="w-8 text-right text-[11px]">{formatTime(currentTime)}</span>
            <div className="flex-1">
              <Slider
                value={[currentTime]}
                max={duration || 100}
                step={1}
                onValueChange={handleSeek}
                className="w-full [&_[role=slider]]:w-4 [&_[role=slider]]:h-4 [&_.bg-primary]:h-1.5 [&_[data-orientation=horizontal]]:h-1.5"
              />
            </div>
            <span className="w-8 text-left text-[11px]">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Contrôles principaux compacts avec marge bottom de 0.3cm */}
        <div className="flex items-center justify-center gap-3" style={{ marginBottom: '0.3cm' }}>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={skipBackward}
            className="relative w-9 h-9 rounded-full bg-background border border-border hover:bg-muted transition-all duration-200"
            aria-label="Reculer de 10 secondes"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="absolute -bottom-0.5 -right-0.5 text-[9px] font-bold text-primary bg-background rounded-full w-4 h-4 flex items-center justify-center border border-primary shadow-sm">
              10
            </span>
          </Button>
          
          <Button 
            onClick={togglePlayPause}
            className="btn-adventure w-12 h-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95"
            aria-label={isPlaying ? "Mettre en pause" : "Lecture"}
          >
            {isPlaying ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" />
            )}
          </Button>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={skipForward}
            className="relative w-9 h-9 rounded-full bg-background border border-border hover:bg-muted transition-all duration-200"
            aria-label="Avancer de 10 secondes"
          >
            <RotateCw className="w-4 h-4" />
            <span className="absolute -bottom-0.5 -right-0.5 text-[9px] font-bold text-primary bg-background rounded-full w-4 h-4 flex items-center justify-center border border-primary shadow-sm">
              10
            </span>
          </Button>
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={togglePlaybackSpeed}
            className="w-9 h-9 rounded-full bg-background border border-border hover:bg-muted transition-all duration-200"
            aria-label={`Vitesse de lecture: ${playbackRate === 0.75 ? '0.75' : playbackRate}x`}
          >
            <span className="text-[10px] font-bold">
              {playbackRate === 0.75 ? '0.75×' : `${playbackRate}×`}
            </span>
          </Button>
        </div>

        {/* Contrôle du volume - caché sur mobile, visible sur desktop */}
        <div className="hidden md:flex items-center justify-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleMute}
            className="w-6 h-6 rounded-full p-0"
            aria-label={isMuted || volume === 0 ? "Activer le son" : "Couper le son"}
          >
            {isMuted || volume === 0 ? (
              <VolumeX className="w-3.5 h-3.5" />
            ) : (
              <Volume2 className="w-3.5 h-3.5" />
            )}
          </Button>
          <Slider
            value={[isMuted ? 0 : volume]}
            max={1}
            step={0.1}
            onValueChange={handleVolumeChange}
            className="w-16 [&_.bg-primary]:h-1"
          />
        </div>
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} />
    </Card>
  );
};

export default AudioPlayer;